// frontend/src/pages/ProjectsPage.tsx - COMPLETE VERSION WITH ALL FUNCTIONALITY
// CRITICAL FIXES: Priority values, field mapping, plus ALL missing functionality restored

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { FolderKanban, Plus } from 'lucide-react';
import { cn, formatDate, truncateText } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';

// FIXED: Use entity-specific hooks
import {
  useProjects,
  useProject,
  useCreateProject,
  useUpdateProject,
  useDeleteProject
} from '@/hooks/useProjects';

import { useOrganizations } from '@/hooks/useOrganizations';
import { useGroups } from '@/hooks/useGroups';
import { useUsers } from '@/hooks/useUsers';

// Import hooks logic for advanced functionality
import { useProjectsLogic } from '@/hooks/useProjectsLogic';

// FIXED: Import the updated ProjectModal
import { ProjectModal } from '@/components/projects/ProjectModal';

// FIXED: Import API types
import type {
  Project,
  Organization,
  Group,
  User,
  NormalizedUser,
  ProjectQueryParams,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  Priority,
  UserRole
} from '@/api/types';

// ✅ FIXED: Define project-specific priority type
type ProjectPriority = 'low' | 'normal' | 'high' | 'critical';

// ===== UTILITY FUNCTIONS =====

// Debounced value hook
function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ===== ORGANIZATION STRATEGY INTERFACE =====

interface OrganizationStrategy {
  mode: 'organization_specific' | 'single_organization';
  canSelectOrganization: boolean;
  defaultOrganization: string | undefined;
}

// ===== ERROR BOUNDARY =====

class ProjectsErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('ProjectsPage Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center">
          <ErrorAlert 
            message="Something went wrong loading the projects page. Please refresh and try again." 
          />
        </div>
      );
    }

    return this.props.children;
  }
}

// ===== STATUS BADGE COMPONENT =====

const StatusBadge: React.FC<{ isActive: boolean }> = ({ isActive }) => (
  <span className={cn(
    'px-2 py-1 text-xs font-medium rounded-full',
    isActive 
      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300'
  )}>
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

// ✅ FIXED: Priority badge component with correct project priorities
const PriorityBadge: React.FC<{ priority: ProjectPriority | Priority }> = ({ priority }) => {
  const priorityConfig = {
    low: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300', label: 'Low' },
    normal: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300', label: 'Normal' }, // ✅ Changed from 'medium'
    high: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300', label: 'High' },
    critical: { color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300', label: 'Critical' }, // ✅ Added 'critical'
    // Legacy support for old Priority type
    medium: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300', label: 'Normal' },
    urgent: { color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300', label: 'Critical' }
  };

  const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.normal;
  
  return (
    <span className={cn('px-2 py-1 text-xs font-medium rounded-full', config.color)}>
      {config.label}
    </span>
  );
};

// ===== PROJECT CARD COMPONENT =====

const ProjectCard: React.FC<{
  project: Project;
  groups: Group[];
  organizations: Organization[];
  onEdit: (project: Project) => void;
  onDelete: (projectId: string) => void;
  onToggleStatus: (projectId: string, isActive: boolean) => void;
  canEdit: boolean;
  canDelete: boolean;
}> = ({ project, groups, organizations, onEdit, onDelete, onToggleStatus, canEdit, canDelete }) => {
  const projectId = project._id || project.id || '';
  
  // ✅ RESTORED: Calculate project statistics
  const projectStats = useMemo(() => {
    const stats = {
      correspondences: (project as any).correspondenceCount || 0,
      responses: (project as any).responseCount || 0,
      keywords: (project as any).keywords?.length || 0,
      conditions: 0
    };
    
    // Calculate total conditions
    if ((project as any).conditions) {
      const conditions = (project as any).conditions;
      stats.conditions = (conditions.sender?.length || 0) + 
                        (conditions.subject?.length || 0) + 
                        (conditions.body?.length || 0) + 
                        (conditions.attachmentName?.length || 0) + 
                        (conditions.attachmentContent?.length || 0);
    }
    
    return stats;
  }, [project]);
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
              {project.name}
            </h3>
            {project.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {truncateText(project.description, 120)}
              </p>
            )}
            
            {/* ✅ RESTORED: Show department if available */}
            {(project as any).department && (
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-2">
                Department: {(project as any).department}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <StatusBadge isActive={project.isActive ?? true} />
            <PriorityBadge priority={(project.priority as ProjectPriority) || 'normal'} />
            
            {/* ✅ RESTORED: Public/Private badge */}
            {project.isPublic && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300">
                Public
              </span>
            )}
            
            {/* ✅ RESTORED: Condition type badge */}
            {(project as any).conditionType && (project as any).conditionType !== 'union' && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300">
                {(project as any).conditionType === 'intersection' ? 'All Match' : 'Any Match'}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {formatDate(project.createdAt || '')}
          </div>
        </div>

        {/* ✅ RESTORED: Project statistics */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-center">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {projectStats.correspondences}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Correspondences</div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {projectStats.responses}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Responses</div>
          </div>
        </div>

        {/* ✅ RESTORED: Keywords display */}
        {(project as any).keywords && (project as any).keywords.length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Keywords ({(project as any).keywords.length}):</div>
            <div className="flex flex-wrap gap-1">
              {(project as any).keywords.slice(0, 3).map((keyword: string, index: number) => (
                <span key={index} className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full dark:bg-green-900/20 dark:text-green-300">
                  {keyword}
                </span>
              ))}
              {(project as any).keywords.length > 3 && (
                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full dark:bg-gray-800 dark:text-gray-400">
                  +{(project as any).keywords.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* ✅ RESTORED: Visibility groups display */}
        {project.visibilityGroups && project.visibilityGroups.length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Visibility Groups:</div>
            <div className="flex flex-wrap gap-1">
              {project.visibilityGroups.slice(0, 3).map((group) => {
                const groupName = typeof group === 'string' 
                  ? groups.find(g => (g._id || g.id) === group)?.name || 'Unknown Group'
                  : group.name || 'Unknown Group';
                
                return (
                  <span 
                    key={typeof group === 'string' ? group : group._id || group.id} 
                    className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full dark:bg-blue-900/20 dark:text-blue-300"
                  >
                    {groupName}
                  </span>
                );
              })}
              {project.visibilityGroups.length > 3 && (
                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full dark:bg-gray-800 dark:text-gray-400">
                  +{project.visibilityGroups.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* ✅ RESTORED: Conditions summary */}
        {projectStats.conditions > 0 && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {projectStats.conditions} condition{projectStats.conditions !== 1 ? 's' : ''} configured
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <span>ID: {projectId.slice(-8)}</span>
            {/* ✅ RESTORED: Last activity indicator */}
            {(project as any).stats?.lastMatchedAt && (
              <span className="ml-2">• Last match: {formatDate((project as any).stats.lastMatchedAt)}</span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {canEdit && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(project)}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onToggleStatus(projectId, !project.isActive)}
                >
                  {project.isActive ? 'Deactivate' : 'Activate'}
                </Button>
              </>
            )}
            {canDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDelete(projectId)}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                Delete
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== MAIN PROJECTS PAGE COMPONENT =====

export const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // ===== STATE MANAGEMENT =====

  const { user: currentUser } = useAuthStore();
  const {
    setApiLoading,
    clearApiLoading,
    setGlobalError,
    clearGlobalError,
    globalError
  } = useAppStore();
  const { confirm } = useConfirmation();

  // ✅ RESTORED: Complete state management
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>(
    (searchParams.get('status') as any) || 'all'
  );
  const [priorityFilter, setPriorityFilter] = useState<'all' | Priority>(
    (searchParams.get('priority') as any) || 'all'
  );
  const [selectedOrganization, setSelectedOrganization] = useState<string>(
    organizationId || searchParams.get('org') || ''
  );

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | undefined>();

  // Debounced search for better performance
  const debouncedSearchTerm = useDebounced(searchTerm, 500);

  // ✅ RESTORED: Organization strategy based on user role
  const organizationStrategy: OrganizationStrategy = useMemo(() => {
    if (currentUser?.role === 'implementor') {
      return {
        mode: 'organization_specific',
        canSelectOrganization: true,
        defaultOrganization: undefined // Implementors must choose
      };
    } else {
      // Admin/contributor users work within their organization
      const userOrgId = typeof currentUser?.organization === 'string' 
        ? currentUser.organization
        : (currentUser?.organization as any)?._id || (currentUser?.organization as any)?.id || '';
        
      return {
        mode: 'single_organization',
        canSelectOrganization: false,
        defaultOrganization: userOrgId
      };
    }
  }, [currentUser]);

  // ===== API HOOKS =====

  // Determine if we should fetch projects
  const shouldFetchProjects = useMemo(() => {
    // Implementors must select an organization first
    if (currentUser?.role === 'implementor') {
      return !!selectedOrganization;
    }
    // Non-implementors can fetch if they have an org
    return !!organizationStrategy.defaultOrganization;
  }, [currentUser?.role, selectedOrganization, organizationStrategy.defaultOrganization]);

  // ✅ RESTORED: Complete query parameters
  const queryParams = useMemo((): ProjectQueryParams | undefined => {
    // Don't build params if we shouldn't fetch
    if (!shouldFetchProjects) return undefined;

    const params: ProjectQueryParams & {
      isActive?: boolean;
      organizationId?: string;
    } = {
      page: 1,
      limit: 50,
      search: debouncedSearchTerm || undefined,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    };

    // Status filter
    if (statusFilter === 'active') params.isActive = true;
    if (statusFilter === 'inactive') params.isActive = false;

    // Priority filter - validate against both old and new priority values
    if (priorityFilter !== 'all' && ['low', 'medium', 'normal', 'high', 'urgent', 'critical'].includes(priorityFilter as Priority)) {
      params.priority = priorityFilter as Priority;
    }

    // Organization filter for implementors
    if (currentUser?.role === 'implementor' && selectedOrganization) {
      params.organizationId = selectedOrganization;
    }


    return params;
  }, [debouncedSearchTerm, statusFilter, priorityFilter, selectedOrganization, currentUser?.role, organizationStrategy, shouldFetchProjects]);

  // Projects query - only fetch if queryParams is defined
  const {
    data: projectsData,
    loading: projectsLoading,
    error: projectsError,
    refetch: refetchProjects
  } = useProjects(queryParams, {
    immediate: shouldFetchProjects
  });

  // ✅ RESTORED: Organizations query for implementors
  const {
    data: organizationsData,
    loading: organizationsLoading
  } = useOrganizations(
    currentUser?.role === 'implementor' ? {} : undefined
  );

  // ✅ RESTORED: Groups query with proper filtering
  const groupsOrgId = selectedOrganization || organizationStrategy.defaultOrganization;
  const shouldFetchGroups = !!currentUser && !!groupsOrgId;

  const {
    data: groupsData,
    loading: groupsLoading,
    error: groupsError
  } = useGroups(
    shouldFetchGroups ? groupsOrgId : undefined,
    shouldFetchGroups ? { type: 'visibility' as const } : undefined,
    {
      immediate: shouldFetchGroups
    }
  );

  // ===== COMPUTED VALUES =====

  // Extract projects from response
  const projects = useMemo((): Project[] => {

    if (!projectsData) return [];
    
    // Cast to any to handle flexible response structure
    const response = projectsData as any;
    
    // Check for projects in response.data
    if (response.data && Array.isArray(response.data)) {
      return response.data as Project[];
    }
    
    // Check for direct projects array
    if (response.projects && Array.isArray(response.projects)) {
      return response.projects as Project[];
    }
    
    // Check if response itself is an array
    if (Array.isArray(response)) {
      return response as Project[];
    }
    
    // Fallback to empty array
    console.warn('No projects found in response structure');
    return [];
  }, [projectsData, selectedOrganization, currentUser?.role]);

  // ✅ RESTORED: Extract organizations
  const organizations = useMemo(() => {
    if (!organizationsData) return [];
    
    const response = organizationsData as any;
    return response.data || response.organizations || response || [];
  }, [organizationsData]);

  // ✅ RESTORED: Extract groups
  const groups = useMemo(() => {
    if (!shouldFetchGroups || !groupsData) return [];
    
    const response = groupsData as any;
    return response.data || response.groups || [];
  }, [shouldFetchGroups, groupsData]);

  const groupsApiAvailable = !groupsError;

  // Permission checks
  const canCreate = useMemo(() => {
    return currentUser?.role === 'implementor' || currentUser?.role === 'admin';
  }, [currentUser?.role]);

  const canEdit = useMemo(() => {
    return currentUser?.role === 'implementor' || currentUser?.role === 'admin';
  }, [currentUser?.role]);

  const canDelete = useMemo(() => {
    return currentUser?.role === 'implementor' || currentUser?.role === 'admin';
  }, [currentUser?.role]);

  // Mutation hooks
  const createProjectMutation = useCreateProject({
    onSuccess: () => {
      setIsModalOpen(false);
      setEditingProject(undefined);
      refetchProjects();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to create project');
    }
  });

  const updateProjectMutation = useUpdateProject({
    onSuccess: () => {
      setIsModalOpen(false);
      setEditingProject(undefined);
      refetchProjects();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to update project');
    }
  });

  const deleteProjectMutation = useDeleteProject({
    onSuccess: () => {
      refetchProjects();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to delete project');
    }
  });

  // ===== EVENT HANDLERS =====

  const handleCreateProject = useCallback(() => {
    if (!canCreate) {
      setGlobalError('You do not have permission to create projects');
      return;
    }

    if (currentUser?.role === 'implementor' && !selectedOrganization) {
      setGlobalError('Please select an organization first');
      return;
    }

    setEditingProject(undefined);
    setIsModalOpen(true);
  }, [canCreate, currentUser?.role, selectedOrganization, setGlobalError]);

  const handleEditProject = useCallback((project: Project) => {
    if (!canEdit) {
      setGlobalError('You do not have permission to edit projects');
      return;
    }

    setEditingProject(project);
    setIsModalOpen(true);
  }, [canEdit, setGlobalError]);

  const handleDeleteProject = useCallback(async (projectId: string) => {
    if (!canDelete) {
      setGlobalError('You do not have permission to delete projects');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Project',
      message: 'Are you sure you want to delete this project? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) {
      return;
    }

    await deleteProjectMutation.mutate(projectId);
  }, [canDelete, deleteProjectMutation, setGlobalError, confirm]);

  const handleToggleProjectStatus = useCallback(async (projectId: string, isActive: boolean) => {
    if (!canEdit) {
      setGlobalError('You do not have permission to modify projects');
      return;
    }

    await updateProjectMutation.mutate({
      id: projectId,
      data: { isActive }
    });
  }, [canEdit, updateProjectMutation, setGlobalError]);

  // ✅ CRITICAL FIX: Updated handleSaveProject with proper field mapping
  const handleSaveProject = useCallback(async (data: any) => {
    
    // ✅ CRITICAL FIX: Transform the data to ensure backend compatibility
    const transformedData = {
      // ✅ Backend expects 'projectName' field
      projectName: data.name || data.projectName,
      // ✅ Also keep 'name' for frontend compatibility
      name: data.name || data.projectName,
      description: data.description,
      // ✅ FIXED: Map priority values - handle both old and new formats
      priority: data.priority === 'medium' ? 'normal' : data.priority || 'normal',
      isActive: data.isActive,
      isPublic: data.isPublic,
      visibilityGroups: data.visibilityGroups || [],
      // Extended properties (if provided)
      ...(data.department && { department: data.department }),
      ...(data.conditions && { conditions: data.conditions }),
      ...(data.exclusionConditions && { exclusionConditions: data.exclusionConditions }),
      ...(data.keywords && { keywords: data.keywords }),
      ...(data.conditionType && { conditionType: data.conditionType }),
      ...(data.scope && { scope: data.scope }),
      // Organization ID for API routing
      ...(data.organizationId && { organizationId: data.organizationId })
    };
    
    
    if (editingProject) {
      await updateProjectMutation.mutate({
        id: editingProject._id || editingProject.id || '',
        data: transformedData as ProjectUpdateRequest
      });
    } else {
      await createProjectMutation.mutate(transformedData as ProjectCreateRequest);
    }
  }, [editingProject, createProjectMutation, updateProjectMutation]);

  const handleRefresh = useCallback(() => {
    clearGlobalError();
    refetchProjects();
  }, [clearGlobalError, refetchProjects]);

  // ✅ RESTORED: Organization selection handler for implementors
  const handleOrganizationChange = useCallback((orgId: string) => {
    setSelectedOrganization(orgId);
    // Update URL params
    const params = new URLSearchParams(searchParams);
    if (orgId) {
      params.set('org', orgId);
    } else {
      params.delete('org');
    }
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  // ===== EFFECTS =====

  // Update URL params when search/filter changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (priorityFilter !== 'all') params.set('priority', priorityFilter);
    if (selectedOrganization) params.set('org', selectedOrganization);
    setSearchParams(params);
  }, [searchTerm, statusFilter, priorityFilter, selectedOrganization, setSearchParams]);

  // Error handling
  useEffect(() => {
    if (projectsError) {
      setGlobalError(projectsError);
    } else {
      clearGlobalError();
    }
  }, [projectsError, setGlobalError, clearGlobalError]);

  // Loading state management
  useEffect(() => {
    if (projectsLoading || 
        organizationsLoading ||
        groupsLoading ||
        createProjectMutation.loading || 
        updateProjectMutation.loading || 
        deleteProjectMutation.loading) {
      setApiLoading('projects', true);
    } else {
      setApiLoading('projects', false);
    }
    
    return () => clearApiLoading();
  }, [
    projectsLoading,
    organizationsLoading,
    groupsLoading,
    createProjectMutation.loading,
    updateProjectMutation.loading,
    deleteProjectMutation.loading,
    setApiLoading,
    clearApiLoading
  ]);

  // ===== PAGE ACTIONS =====

  const actions = useMemo(() => (
    <ProjectsErrorBoundary>
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <Input
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
          />
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* ✅ RESTORED: Priority filter with correct values */}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
            {/* Legacy support */}
            <option value="medium">Medium (Legacy)</option>
            <option value="urgent">Urgent (Legacy)</option>
          </select>

          {/* ✅ RESTORED: Organization selector for implementors */}
          {currentUser?.role === 'implementor' && (
            <select
              value={selectedOrganization}
              onChange={(e) => handleOrganizationChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="">Select Organization</option>
              {organizations.map((org: Organization) => (
                <option key={org._id || org.id} value={org._id || org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={projectsLoading}
          >
            {projectsLoading ? <LoadingSpinner size="sm" /> : 'Refresh'}
          </Button>
          
          {canCreate && (
            <Button onClick={handleCreateProject}>
              Create Project
            </Button>
          )}
        </div>
      </div>
    </ProjectsErrorBoundary>
  ), [
    searchTerm,
    statusFilter,
    priorityFilter,
    selectedOrganization,
    currentUser?.role,
    organizations,
    canCreate,
    projectsLoading,
    handleOrganizationChange,
    handleRefresh,
    handleCreateProject
  ]);

  // ===== RENDER =====

  return (
    <ProjectsErrorBoundary>
      <PageLayout
        title="Projects"
        subtitle="Organize and classify email correspondence"
        actions={actions}
      >
        {/* Show global error */}
        {globalError && (
          <div className="mb-6">
            <ErrorAlert
              message={globalError}
              onClose={clearGlobalError}
            />
          </div>
        )}

        {/* ✅ Show organization selection prompt for implementors with no org selected */}
        {currentUser?.role === 'implementor' && !selectedOrganization ? (
          <div className="space-y-6">
            {/* Prominent Organization Selector */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center space-x-4">
                <div className="text-4xl">🏢</div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Organization *
                  </label>
                  <select
                    value={selectedOrganization}
                    onChange={(e) => {
                      const newOrg = e.target.value;
                      setSelectedOrganization(newOrg);
                      // Update URL params
                      if (newOrg) {
                        setSearchParams({ org: newOrg });
                      } else {
                        setSearchParams({});
                      }
                    }}
                    className="flex-1 max-w-md px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Organization...</option>
                    {organizations.map((org: Organization) => (
                      <option key={(org as any)._id || org.id} value={(org as any)._id || org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {!selectedOrganization && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 ml-16">
                  Please select an organization to view and manage projects.
                </p>
              )}
            </div>

            {/* Additional Context Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
              <div className="text-4xl mb-4">📁</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Select an Organization
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-2 max-w-md mx-auto">
                Projects are organization-specific and help you organize and classify email correspondence.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Select an organization above to view and manage its projects.
              </p>
            </div>
          </div>
        ) : projectsLoading && !projects.length ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading projects...</p>
            </div>
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <EmptyState
              icon={<FolderKanban size={24} />}
              title="No projects found"
              description={
                searchTerm || statusFilter !== 'all' || priorityFilter !== 'all'
                  ? 'No projects match your current filters. Try adjusting your search criteria.'
                  : 'Create projects to organize and classify email correspondence.'
              }
              action={canCreate ? {
                label: 'Create Your First Project',
                onClick: handleCreateProject,
                icon: <Plus size={16} />,
              } : undefined}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {projects.map((project) => {
              const projectId = project._id || project.id || '';
              if (!projectId) {
                console.warn('Skipping invalid project:', project);
                return null;
              }
              
              return (
                <ProjectCard
                  key={projectId}
                  project={project}
                  groups={groups}
                  organizations={organizations}
                  onEdit={handleEditProject}
                  onDelete={handleDeleteProject}
                  onToggleStatus={handleToggleProjectStatus}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              );
            })}
          </div>
        )}

        {/* ✅ RESTORED: Complete ProjectModal with all props */}
        <ProjectModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingProject(undefined);
          }}
          onSave={handleSaveProject}
          project={editingProject}
          groups={groups}
          organizations={organizations}
          groupsApiAvailable={groupsApiAvailable}
          loading={createProjectMutation.loading || updateProjectMutation.loading}
          currentUser={currentUser}
          selectedOrganization={selectedOrganization || undefined}
        />
      </PageLayout>
    </ProjectsErrorBoundary>
  );
};